package com.htmlagent.site.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.shared.auth.ApprovalPrompt
import com.htmlagent.site.components.sections.landing.PRODUCT_NAME
import com.htmlagent.site.components.widgets.Mark
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.Identity
import com.htmlagent.site.theme.ErrorStyle
import com.htmlagent.site.theme.GateBrandNameStyle
import com.htmlagent.site.theme.GateBrandStyle
import com.htmlagent.site.theme.GateBrandTaglineStyle
import com.htmlagent.site.theme.GateCardStyle
import com.htmlagent.site.theme.GateMarkTileStyle
import com.htmlagent.site.theme.GateMarkWrapStyle
import com.htmlagent.site.theme.GateFormStyle
import com.htmlagent.site.theme.GateGlowStyle
import com.htmlagent.site.theme.GateGuestVariant
import com.htmlagent.site.theme.GateInputVariant
import com.htmlagent.site.theme.GateLabelStyle
import com.htmlagent.site.theme.GatePrimaryVariant
import com.htmlagent.site.theme.GateStyle
import com.htmlagent.site.theme.ThemeTokensStyle
import com.htmlagent.site.theme.GateWarningStyle
import com.htmlagent.site.theme.GateWhyDetailStyle
import com.htmlagent.site.theme.GateWhyStyle
import com.htmlagent.site.theme.Space
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Button as DomButton
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Ties the visible label to the field. There is one gate on screen at a time. */
private const val EMAIL_FIELD_ID = "gate-email"

/**
 * The entrance: an email to send the access link to, or going on as a guest.
 *
 * It is also the first screen of the installed app, so the product introduces itself above the
 * card - the mark, the name and the promise - and the card is only the form. The notice is a
 * single line with its reason one tap away: at full length it was five lines before the door.
 *
 * With [sentTo] set the card says where the link went and shows the [match] number, and the page
 * signs in on its own once the link is opened or approved. With [approve] it is the other end: the
 * link opened in a browser other than the one that asked, where the person picks that number to
 * let the waiting one in, and [approved] says it is done. Neither of those two offers going on as
 * a guest: this browser is not the one signing in. [serverError] is what the API answered,
 * already in words. It uses a real `<form>` so Enter submits, the phone
 * keyboard shows "go" and password managers understand the field. It carries `novalidate`
 * because with `type="email"` the browser blocks the `submit` silently and our own check, with
 * its own message, would never get to run.
 */
@Composable
@Suppress("LongParameterList") // The gate's states come from the page: values, not dependencies.
fun Gate(
    onEnter: (String) -> Unit,
    onGuest: () -> Unit,
    initialEmail: String = "",
    serverError: String? = null,
    sending: Boolean = false,
    sentTo: String? = null,
    match: Int? = null,
    onOtherEmail: () -> Unit = {},
    approve: ApprovalPrompt? = null,
    onApprove: (Int) -> Unit = {},
    approved: Boolean = false,
) {
    val t = LocalStrings.current
    var draft by remember(initialEmail) { mutableStateOf(initialEmail) }
    var typingError by remember { mutableStateOf<String?>(null) }
    val error = typingError ?: serverError

    fun submit() {
        val value = draft.trim()
        if (Identity.looksLikeEmail(value)) {
            typingError = null
            onEnter(value)
        } else {
            typingError = t.gate.invalidEmail
        }
    }

    Box(GateStyle.toModifier().then(ThemeTokensStyle.toModifier())) {
        Column(
            modifier = GateBrandStyle.toModifier(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Div(GateMarkWrapStyle.toModifier().toAttrs()) {
                Div(GateGlowStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
                Span(GateMarkTileStyle.toModifier().toAttrs()) { Mark() }
            }
            H1(GateBrandNameStyle.toModifier().toAttrs()) {
                Text(PRODUCT_NAME)
            }
            SpanText(t.gate.tagline, GateBrandTaglineStyle.toModifier())
        }

        Column(
            modifier = GateCardStyle.toModifier(),
            verticalArrangement = Arrangement.spacedBy(Space.Lg),
        ) {
            if (approved) {
                LinkApproved()
            } else if (approve != null) {
                ApproveLink(approve.email, approve.choices, onPick = onApprove, onCancel = onOtherEmail)
            } else if (sentTo != null && match != null) {
                WaitingForLink(sentTo, match, onOtherEmail)
            } else Form(
                attrs = GateFormStyle.toModifier().toAttrs {
                    attr("novalidate", "")

                    // The `Form` DSL exposes no `onSubmit`; this event is what makes Enter submit.
                    ref { form ->
                        val listener: (org.w3c.dom.events.Event) -> Unit = { event ->
                            event.preventDefault()
                            submit()
                        }
                        form.addEventListener("submit", listener)
                        onDispose { form.removeEventListener("submit", listener) }
                    }
                },
            ) {
                // A visible label now that the card has no title to say what the field is for.
                Label(forId = EMAIL_FIELD_ID, attrs = GateLabelStyle.toModifier().toAttrs()) {
                    Text(t.gate.emailLabel)
                }
                TextInput(
                    text = draft,
                    onTextChange = { draft = it },
                    modifier = Modifier.attrsModifier {
                        attr("id", EMAIL_FIELD_ID)
                        attr("type", "email")
                        attr("autocomplete", "email")
                        // Only field on screen: asking for a click before typing is friction for nothing.
                        // Not on a touch screen, where focusing it opens the keyboard over half the
                        // screen - and in the installed app, over the splash still playing.
                        if (window.matchMedia("(pointer: fine)").matches) attr("autofocus", "")
                        if (error != null) attr("aria-invalid", "true")
                    },
                    variant = GateInputVariant,
                    placeholder = t.gate.emailPlaceholder,
                    focusBorderColor = null,
                )

                error?.let { message ->
                    // The border color cannot be the only indicator.
                    Box(ErrorStyle.toModifier().attrsModifier { attr("role", "alert") }) {
                        SpanText(message)
                    }
                }

                Button(
                    onClick = { submit() },
                    enabled = !sending,
                    modifier = Modifier.attrsModifier { attr("type", "submit") },
                    variant = GatePrimaryVariant,
                ) {
                    SpanText(if (sending) t.gate.sending else t.gate.submit)
                }
            }

            // Approving signs in another window: this one has no door of its own to offer. And once
            // the link is on its way, the notice about sending one has nothing left to explain.
            if (approve == null && !approved) GuestAndNotice(onGuest, notice = sentTo == null)
        }
    }
}

/** Going on as a guest, and the one-line notice with its reason one tap away. */
@Composable
private fun GuestAndNotice(onGuest: () -> Unit, notice: Boolean) {
    val t = LocalStrings.current
    var explained by remember { mutableStateOf(false) }

    // Second and quieter, but present: nobody should have to say who they are before
    // seeing whether this works. Whoever takes it gets a key of their own, and the
    // sessions panel is where they are told what that key is.
    Button(
        onClick = { onGuest() },
        modifier = Modifier.attrsModifier { attr("type", "button") },
        variant = GateGuestVariant,
    ) {
        SpanText(t.gate.guestSubmit)
    }

    // The warning stays on the screen, in one line; the full reason is one tap away.
    if (notice) Div(GateWarningStyle.toModifier().toAttrs()) {
        Text(t.gate.shortNotice + " ")
        // A bare `<button>` and not Silk's: that one keeps a box of its own and the link
        // dropped to a line by itself.
        DomButton(
            attrs = GateWhyStyle.toModifier().toAttrs {
                attr("type", "button")
                attr("aria-expanded", explained.toString())
                onClick { explained = !explained }
            },
        ) {
            Text(t.gate.why)
        }
        if (explained) {
            Div(GateWhyDetailStyle.toModifier().toAttrs()) { Text(t.gate.notALoginNotice) }
        }
    }
}
